import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { LanguagePicker } from '@rs/ui'
import { tDynamic } from '@rs/ui/i18n'

/**
 * ══ MÀN ĐĂNG NHẬP — HAI LỰA CHỌN (E13 sửa 19/10/2026) ════════════════════════════════════════════════════════════
 *
 * ① "Đăng nhập với FADO SSO" — OIDC + PKCE kiểu BFF (`auth.ts`), như từ đợt 0.
 * ② "Đăng nhập với tài khoản" — email + mật khẩu do rs-erp tự quản (argon2id, `@rs/identity` ▸ `passwordLogin`). Anh mở
 *    lại ngày 19/10/2026; trước đó E13 (03/09) chỉ cho SSO. Bấm ② mới bung ô email / mật khẩu — màn đầu là hai nút ngang
 *    hàng, đúng yêu cầu "vào màn hình thì có 2 option".
 *
 * So với bản cũ `proship-trans/src/pages/Login.tsx` (email · mật khẩu · Ô CHỌN VAI · "Xem thử" · quên mật khẩu):
 *   · ô email + mật khẩu: CÓ lại (②).
 *   · Ô chọn vai + "Xem thử" (vào không cần mật khẩu): VẪN BỎ — mục **C4** của sổ đăng ký, lỗ lớn nhất của bản cũ (gõ email
 *     lạ rồi tự phong admin). Vai luôn lấy từ `app_user.role` ở máy chủ.
 *   · "Để trống mật khẩu là vào luôn" (`Login.tsx:118`): KHÔNG chép — cửa mở cho mọi email.
 *   · "Quên mật khẩu? — đặt lại ngay" (tự đặt lại không cần gì): KHÔNG chép, cùng lý do. Quên thì vào bằng FADO SSO rồi đổi
 *     ở 🔒 Mật khẩu, hoặc nhờ admin / KTT cấp lại mật khẩu tạm (`PasswordCard.tsx`).
 * Thầu phụ (E15) vẫn đi cổng RIÊNG `/ncc/login` (bảng `vendor_user`) — ② ở đây chỉ nhận tài khoản nội bộ.
 *
 * Vẫn GIỮ `<LanguagePicker />` đủ 3 ngôn ngữ (B6 · D03b §7.1) — khách chưa đăng nhập vẫn đổi được,
 * và lựa chọn giữ tạm ở `localStorage` rồi được `auth.tsx` chuyển lên phiên máy chủ ngay sau khi vào.
 */

/**
 * ══ MÃ LỖI `?loi=` → CÂU CHỮ ═══════════════════════════════════════════════════════════════════
 * ⚠ Bản trước của tệp này khẳng định "bảy mã là TOÀN BỘ" — SAI, thiếu ba mã THẬT (Q3):
 *   `luot-dang-nhap-het-han` (`auth.ts:258`) · `sub-khong-khop` · `thieu-dinh-danh` (`ssoLogin.ts:12-14`).
 *
 * ⚠ Vòng trước vá bảng NHƯNG LƯỚI CANH VẪN MÙ VỚI MỘT NHÁNH (ĐK1): `qua-nhieu-luot-dang-nhap` đi
 *   từ `/sso/start` (`auth.ts:169`) — nhánh đó `redirect()` THẲNG, **không qua `fail()`**, và
 *   `bd.reason` thuộc `StartOidcTxResult` chứ không thuộc `SsoDenyReason` ⇒ cả ràng buộc kiểu lẫn
 *   hai lưới quét nguồn đều không thấy. Nay nguồn ④ có lưới riêng, cùng kiểu ràng buộc.
 *
 * Tập giá trị `loi` thật gồm BỐN nguồn:
 *   ① `SsoDenyReason` — năm mã từ chối của `ssoLogin()`, đều nói về TÀI KHOẢN của người dùng ⇒
 *      LUÔN phải có câu riêng. `login.test.tsx` ràng buộc bảng này với CHÍNH KIỂU đó, nên máy chủ
 *      thêm một mã mới là `tsc -b` ĐỎ, không im lặng rơi về câu chung.
 *   ② năm mã của chặng gọi lại (`auth.ts` — `fail('…')`), đều nói về LƯỢT đăng nhập.
 *      `login.test.tsx` quét thẳng mã nguồn `auth.ts` để bắt mã mới.
 *   ③ mã lỗi giao thức OIDC do chính IdP trả (`VALID_OIDC_ERROR_CODES`, `auth.ts:120-129`) và
 *      `idp-tu-choi` (`auth.ts:131`, gom mọi mã không chuẩn). Nhóm này CỐ Ý dùng câu chung
 *      `ssoError` — chúng là lỗi giao thức giữa hai máy chủ, đọc tên mã ra không giúp người dùng
 *      làm được gì thêm.
 *   ④ mã CHẶN Ở CỬA VÀO `/sso/start` — `StartOidcTxResult` (`oidcTx.ts:26-28`). Ràng buộc kiểu +
 *      quét nguồn y hệt ①.
 *
 * ⚠ CÂU CHỮ NẰM Ở KHO CHUỖI, KHÔNG Ở ĐÂY (Task 21) — `login.loi.<mã>`, đủ vi/en/zh. Task 19 để
 *   chúng là chuỗi cứng tiếng Việt vì i18next chưa dựng; Task 20 nhận vào kho; Task 21 rút chuỗi
 *   cứng đi và giữ lại đúng thứ KHÔNG dịch được: **tập MÃ có câu riêng**. Mã ngoài danh sách này
 *   rơi về câu chung `login.ssoError` (xem `cauLoi`).
 *
 * ⚠ MẢNG chứ KHÔNG phải object literal — đóng luôn lớp lỗi Q4 (`__proto__` · `constructor` ·
 *   `toString`… kế thừa từ `Object.prototype`) mà không cần `Object.hasOwn`: `MA_LOI.includes()`
 *   không đọc gì từ chuỗi nguyên mẫu.
 */
export const ERROR_CODES = [
  // ① SsoDenyReason — về TÀI KHOẢN
  'khong-co-trong-app-user',
  'khoa-tai-khoan',
  'email-chua-xac-thuc',
  // ⚠ CHỐT CHỐNG CHIẾM TÀI KHOẢN (`ssoLogin.ts:76-99`): email khớp một dòng ĐÃ ghim `sub` KHÁC.
  //   Bấm đăng nhập lại KHÔNG BAO GIỜ qua được — khuyên "thử lại" là lời khuyên SAI, người dùng
  //   sẽ bấm mãi thay vì gọi quản trị. Câu chữ phải nói thẳng điều đó.
  'sub-khong-khop',
  'thieu-dinh-danh',
  // ② chặng gọi lại — về LƯỢT đăng nhập, bấm lại là qua
  'state-khong-khop',
  'mat-phien-dang-nhap',
  'luot-dang-nhap-het-han',
  'doi-ma-that-bai',
  'thieu-ma-uy-quyen',
  // ④ chặn ở CỬA VÀO `/sso/start` (`oidcTx.ts:39-44`, ngưỡng `OIDC_TX_MAX_PER_IP` = 50 lượt ĐANG
  //    MỞ trên MỘT IP). ⚠ Đây là mã DUY NHẤT mà "bấm đăng nhập lại" là lời khuyên SAI THEO THỜI
  //    GIAN: bấm lại chỉ tạo thêm một lượt đang mở, không bao giờ qua cho tới khi các lượt cũ hết
  //    hạn — đúng `OIDC_TX_TTL` = 10 phút, cũng là `Retry-After: 600` mà máy chủ trả cho MÁY
  //    (`auth.ts:168`). Câu chữ phải nói với NGƯỜI điều máy chủ đã nói với máy: PHẢI CHỜ.
  //    Ngưỡng đếm theo IP nên cả văn phòng sau một NAT dùng chung hạn mức — sau một sự cố IdP thì
  //    50 lượt dở dang trong 10 phút là con số đạt được, không phải giả thuyết.
  'qua-nhieu-luot-dang-nhap',
] as const

/**
 * ⚠ Tra bằng `MA_LOI.includes`, KHÔNG bằng `object[ma]` (Q4 — lỗi thật, ai cũng dựng được link
 * khai thác). Bảng cũ là object literal nên nó KẾ THỪA `__proto__` · `constructor` · `toString` ·
 * `valueOf`… từ `Object.prototype`: `BANG['constructor'] ?? t('ssoError')` trả HÀM `Object`
 * (truthy ⇒ `??` không cứu), React ném lỗi khi render một hàm, không có error boundary ⇒ **màn
 * đăng nhập trắng trơn**. Mảng không có cửa đó.
 *
 * ⚠ `tDynamic` chứ không `t()` thẳng: khoá dựng từ giá trị `?loi=` lúc chạy nên không thể là literal.
 *   Danh sách trắng `MA_LOI` đứng TRƯỚC nên không có chuỗi lạ nào đi tới `t()` — nếu không,
 *   `missingKeyHandler` (ném ở dev) biến một tham số URL bất kỳ thành lỗi sập màn đăng nhập.
 */
const errorMessage = (t: TFunction, code: string): string | undefined =>
  (ERROR_CODES as readonly string[]).includes(code) ? tDynamic(t, `login.loi.${code}`) : undefined

/**
 * Mã lỗi về TÀI KHOẢN FADO (nhóm ① — `SsoDenyReason`): bấm lại bằng ĐÚNG tài khoản đó không bao giờ qua. Mà trình duyệt
 * còn phiên FADO (12 giờ) thì `/sso/start` trần được FADO trả thẳng tài khoản cũ, không hỏi gì ⇒ người dùng kẹt vòng
 * "bấm → cùng lỗi" (phản ánh 24/10 trên rs.woka.io: "không thao tác login được nữa"). Gặp nhóm này thì nút SSO mang
 * `prompt=login` để FADO hỏi lại, chọn được tài khoản khác. Nhóm ② ④ là lỗi của LƯỢT, bấm lại bình thường là qua.
 */
export const ACCOUNT_ERROR_CODES: readonly string[] = [
  'khong-co-trong-app-user', 'khoa-tai-khoan', 'email-chua-xac-thuc', 'sub-khong-khop', 'thieu-dinh-danh',
]

/**
 * Cửa bắt đầu đăng nhập, KÈM CHỖ ĐANG ĐỨNG (`next`) — màn đăng nhập vẽ ngay tại đường dẫn người dùng đang mở khi phiên
 * hết giữa chừng (router), nên đăng nhập xong máy chủ đưa về đúng đó thay vì `/` (rà Lô 2, 17/10). Đang ở `/login`
 * hay `/` thì không kèm. ⚠ Máy chủ mới là chốt (`safeReturnPath` — chỉ đường dẫn nội bộ); ở đây chỉ gửi đường dẫn tương đối.
 * `forceLogin` ⇒ thêm `prompt=login` (máy chủ chuyển tiếp sang FADO, `auth.ts` ▸ `/sso/start`).
 */
export const ssoStartHref = (pathname: string, search: string, forceLogin = false): string => {
  const q: string[] = []
  if (pathname !== '/' && pathname !== '/login' && pathname.startsWith('/')) q.push(`next=${encodeURIComponent(`${pathname}${search}`)}`)
  if (forceLogin) q.push('prompt=login')
  return q.length === 0 ? '/api/v1/auth/sso/start' : `/api/v1/auth/sso/start?${q.join('&')}`
}

/**
 * Mã lỗi `?loi=` ĐỌC MỘT LẦN. L55 (rà 16/10, lỗi riêng rs-erp — bản cũ không có SSO): `?loi=` RỖNG từng hiện câu chung
 * "Không đăng nhập được…" — tham số rỗng không phải một lỗi, bỏ qua.
 */
const errorOfUrl = (): string | null => {
  const v = new URLSearchParams(location.search).get('loi')
  return v === null || v.trim() === '' ? null : v
}

/** Chuỗi truy vấn hiện tại BỎ `loi` — để gỡ mã khỏi thanh địa chỉ và để `next` của nút đăng nhập không mang nó theo. */
export const searchWithoutError = (search: string): string => {
  const p = new URLSearchParams(search)
  p.delete('loi')
  const q = p.toString()
  return q === '' ? '' : `?${q}`
}

/**
 * ② Ô email + mật khẩu. Gọi `fetch` thẳng, KHÔNG qua `call()`: `call` coi 401 là "hết phiên giữa chừng" và phát sự kiện
 * đăng xuất — ở đây 401 chỉ là "sai email hoặc mật khẩu". Câu lỗi lấy nguyên của máy chủ (cùng câu cho email lạ và sai mật
 * khẩu — chống dò). Vào được thì tải lại trang: `AuthProvider` hỏi `/auth/me` từ đầu, router tự vẽ màn ép đổi nếu đang
 * dùng mật khẩu tạm.
 */
const AccountLoginForm = ({ returnTo, onBack }: { returnTo: string; onBack: () => void }): JSX.Element => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (submitting) return
    setErr(null)
    setSubmitting(true)
    try {
      const res = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null) as { message?: string } | null
        setErr(body?.message ?? t('login.ssoError'))
        return
      }
      location.assign(returnTo)
    } catch {
      setErr(t('login.ncc.netError'))
    } finally {
      setSubmitting(false)
    }
  }

  const inCls = 'w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100'
  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-4" data-testid="form-tai-khoan">
      <div>
        <label htmlFor="login-email" className="mb-1 block text-xs font-semibold text-slate-500">{t('login.email')}</label>
        <input id="login-email" type="email" value={email} autoComplete="username" required autoFocus
               onChange={(e) => { setEmail(e.target.value) }} className={inCls} />
      </div>
      <div>
        <label htmlFor="login-pw" className="mb-1 block text-xs font-semibold text-slate-500">{t('login.password')}</label>
        <input id="login-pw" type="password" value={password} autoComplete="current-password" required
               onChange={(e) => { setPassword(e.target.value) }} className={inCls} />
      </div>
      {err !== null && err !== '' && (
        <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</p>
      )}
      <button type="submit" disabled={submitting}
              className="w-full rounded-lg bg-gradient-to-r from-brand-500 to-brand-600 px-4 py-2.5 text-sm font-bold text-white hover:from-brand-600 hover:to-brand-700 disabled:opacity-40">
        {submitting ? t('login.ncc.submitting') : t('login.signIn')}
      </button>
      <p className="text-[13px] text-slate-500">{t('login.accountHint')}</p>
      <button type="button" onClick={onBack} className="text-xs font-semibold text-slate-500 hover:underline">
        {t('login.back')}
      </button>
    </form>
  )
}

export interface LoginPageProps {
  /** Chỉ để test thay bằng một nút giả; mặc định LÀ `<LanguagePicker />` thật. */
  languagePicker?: React.ReactNode
  /**
   * Phiên vừa hết GIỮA CHỪNG (L18) — router truyền vào khi một lệnh nhận 401. Hiện câu có sẵn
   * `login.loi.mat-phien-dang-nhap` ("Phiên đăng nhập đã hết hạn. Bấm đăng nhập lại."), đúng ý câu
   * báo của bản cũ (`proship-trans/src/lib/auth.tsx:103-107`) mà không cần khoá mới.
   */
  expired?: boolean
}

export const LoginPage = ({ languagePicker, expired = false }: LoginPageProps = {}): JSX.Element => {
  const { t } = useTranslation()
  const [urlError] = useState(errorOfUrl)
  const errCode = urlError ?? (expired ? 'mat-phien-dang-nhap' : null)
  const switchAccount = errCode !== null && ACCOUNT_ERROR_CODES.includes(errCode)
  const startHref = ssoStartHref(location.pathname, searchWithoutError(location.search), switchAccount)
  const [mode, setMode] = useState<'choose' | 'account'>('choose')
  /* Đăng nhập bằng tài khoản xong thì tải lại ĐÚNG chỗ đang đứng (hết phiên giữa chừng — như `next` của SSO); đứng ở `/login`
     thì về trang gốc. Chỉ đường dẫn tương đối của chính trang này — không có chỗ nào cho URL ngoài lọt vào. */
  const returnTo = location.pathname === '/login' ? '/' : `${location.pathname}${searchWithoutError(location.search)}`
  /* L55: hiện xong thì GỠ `?loi=` khỏi thanh địa chỉ (`replaceState`, không thêm mục lịch sử) — tải lại trang hay đánh
     dấu trang không còn thấy lỗi cũ. Câu lỗi vẫn đứng trên màn (đã đọc vào state). */
  useEffect(() => {
    if (!new URLSearchParams(location.search).has('loi')) return
    history.replaceState(history.state, '', `${location.pathname}${searchWithoutError(location.search)}${location.hash}`)
  }, [])

  return (
    /**
     * ⭐ DỰNG KHUNG NHẬN DIỆN 18/09/2026 — trước đó màn này là một `<main style={{…}}>` trần: không
     * một lớp nào, không logo, không tên công ty, và nút đăng nhập là thẻ `<a>` không có vùng bấm.
     * Đây là màn ĐẦU TIÊN mọi người dùng và mọi thầu phụ nhìn thấy, mà nó trông như trang chưa nạp
     * CSS. Việc BỎ ô email / mật khẩu / chọn vai là CỐ Ý (E13 · C4 — đăng nhập đi qua FADO SSO);
     * bỏ luôn khung nhận diện thì không có quyết định nào cho phép, và `/tra-cuu` cùng đợt lại
     * dựng đủ khung — nên đây là bỏ sót, không phải chủ ý.
     *
     * Khuôn chép bản cũ (`Login.tsx:160-166`): thẻ trắng bo góc + dải gradient mang logo + tên
     * ứng dụng, nút chính chiếm trọn bề ngang. Nền gradient phủ trọn khung + thẻ căn giữa dọc —
     * `Login.tsx:150-151`.
     */
    <main className="min-h-full grid place-items-center bg-gradient-to-br from-brand-50 via-white to-sky-100 p-4">
      <div className="w-full max-w-sm">
        {/* Giữ thẻ `<header>`: bộ chọn ngôn ngữ đứng trên thẻ đăng nhập, đúng bản cũ
            (`Login.tsx:152-159`), và ca kiểm neo vào đúng thẻ này. Nền sáng ⇒ `tone="light"`. */}
        <header className="mb-3 flex justify-end">
          {languagePicker ?? <LanguagePicker tone="light" />}
        </header>
        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl">
          <div className="bg-gradient-to-r from-brand-500 to-sky-500 px-6 py-7 text-center">
            <img src="/RT-LOGO-header.jpg" alt={t('app.name')}
                 className="mx-auto mb-2 h-12 w-auto rounded-lg bg-white object-contain px-2 py-1" />
            {/* Dòng phụ dưới logo là KHẨU HIỆU `app.tagline`, không lặp tên ứng dụng (đã nằm ở alt
                logo) — bản cũ `Login.tsx:163`. */}
            <div className="text-sm text-brand-50">{t('app.tagline')}</div>
          </div>
          <div className="space-y-4 px-6 py-6">
            <h1 className="text-lg font-bold text-slate-800">{t('login.title')}</h1>
            {errCode !== null && (
              <p role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">
                {errorMessage(t, errCode) ?? t('login.ssoError')}
              </p>
            )}
            {mode === 'choose' ? (
              <>
                {/* ① Chuyển hướng CẢ TRANG, KHÔNG `fetch`: luồng OIDC là điều hướng trình duyệt thật. */}
                <a href={startHref} data-testid="dang-nhap-fado"
                   className="block w-full rounded-lg bg-gradient-to-r from-brand-500 to-brand-600 px-4 py-2.5 text-center text-sm font-bold text-white hover:from-brand-600 hover:to-brand-700">
                  {t('login.loginWithFado')}
                </a>
                <p className="text-[13px] text-slate-500">{switchAccount ? t('login.ssoSwitchHint') : t('login.ssoHint')}</p>
                <div className="flex items-center gap-3 text-[11px] text-slate-400" aria-hidden="true">
                  <span className="h-px flex-1 bg-slate-200" />{t('login.or')}<span className="h-px flex-1 bg-slate-200" />
                </div>
                {/* ② */}
                <button type="button" data-testid="dang-nhap-tai-khoan" onClick={() => { setMode('account') }}
                        className="block w-full rounded-lg border border-brand-500 bg-white px-4 py-2.5 text-center text-sm font-bold text-brand-600 hover:bg-brand-50">
                  {t('login.withAccount')}
                </button>
              </>
            ) : (
              <AccountLoginForm returnTo={returnTo} onBack={() => { setMode('choose') }} />
            )}
          </div>
        </div>
      </div>
    </main>
  )
}
